import { registerPropertyManagerSection } from '@/core';
import AssemblyMatePropertySection from '@/components/editor/property-manager/assembly-mate/AssemblyMatePropertySection.vue';
import { resolveAssemblyMateController } from '@/utils';
import { useEditorAssemblyWorkflow } from '@/views/editor/useEditorAssemblyWorkflow';

/**
 * 解析标准配合确认按钮禁用状态。
 *
 * 设计说明：
 * Mate 的合法性、求解状态和可提交状态均由 cad-core 控制器输出；注册层只把它映射为 PropertyManager
 * section 扩展点，避免宿主属性栏硬编码 Mate 业务字段。
 *
 * @param context PropertyManager 上下文。
 * @returns `true` 表示禁用确认，`false` 表示允许确认。
 */
function resolveAssemblyMateConfirmDisabled(context: { renderer?: unknown }): boolean {
  const state = resolveAssemblyMateController(context.renderer)?.getViewModel?.().state;
  const candidateCount = state?.candidates?.length ?? 0;
  if (candidateCount === 0) {
    return false;
  }
  return state?.canSubmit !== true;
}

/**
 * 订阅 Mate 确认状态变化。
 *
 * @param context PropertyManager 上下文。
 * @param listener 状态变化监听器。
 * @returns 取消订阅函数；控制器不存在时返回空。
 */
function subscribeAssemblyMateConfirmState(
  context: { renderer?: unknown },
  listener: () => void,
): (() => void) | void {
  return resolveAssemblyMateController(context.renderer)?.subscribeStateChanged?.(() => {
    listener();
  });
}

registerPropertyManagerSection({
  id: 'assembly-mate',
  title: '配合',
  order: 45,
  component: AssemblyMatePropertySection,
  exclusive: true,
  actions: {
    confirm: true,
    cancel: true,
  },
  resolveConfirmDisabled: resolveAssemblyMateConfirmDisabled,
  subscribeConfirmState: subscribeAssemblyMateConfirmState,
  isApplicable: (context) =>
    context.activeCommand === 'assembly-mate'
    || Boolean(resolveAssemblyMateController(context.renderer)?.isActive?.()),
  onConfirm: () => {
    void useEditorAssemblyWorkflow().confirmAssemblyMateCommand().catch((error) => {
      if (typeof window !== 'undefined' && typeof window.reportError === 'function') {
        window.reportError(error);
      }
    });
  },
  onCancel: () => {
    useEditorAssemblyWorkflow().cancelAssemblyMateCommand();
  },
});
